import { useMsal } from '@azure/msal-react'
import {
  faChevronRight,
  faExpand,
  faPaintBrush,
  faPencil,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Button,
  Group,
  Image,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PendingIcon from '../../assets/statusIcons/Pending.svg'
import { RequestChangesModal } from '../../components/RequestChangesModal'
import { useSessionInfo } from '../../contexts/SessionContext'
import { getAdsPendingDesign } from '../../data/admin.service'
import {
  approveDesignRequest,
  fetchDesignRequests,
  requestDesignChanges,
} from '../../data/designRequest.service'
import { DesignRequest } from '../../models/DesignRequest'
import { AdStatuses, COLORS } from '../../utils/constants'
import { convertServerDateToLocal } from '../../utils/dateUtils'
import DashboardHeader from './DashboardHeader'

interface DesignRequestsPageProps {
  isAdmin?: boolean
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case AdStatuses.PendingAdvertiserApproval:
      return PendingIcon
    case AdStatuses.PendingDesign:
      return null
    default:
      return null
  }
}

const getStatusTitle = (status: string, isAdmin: boolean) => {
  switch (status) {
    case AdStatuses.PendingAdvertiserApproval:
      return isAdmin ? 'Pending Approval' : 'Pending Your Approval'
    case AdStatuses.PendingDesign:
      return isAdmin ? 'Pending Design Requests' : 'Pending Design'
    default:
      return 'Design Requests'
  }
}

export default function DesignRequestsPage({
  isAdmin = false,
}: DesignRequestsPageProps) {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const navigate = useNavigate()
  const [designRequests, setDesignRequests] = useState<DesignRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedAd, setSelectedAd] = useState<string | null>(null)
  const [requestingChanges, setRequestingChanges] = useState(false)
  const [showChangesModal, setShowChangesModal] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null,
  )
  const [approvingRequestId, setApprovingRequestId] = useState<number | null>(
    null,
  )

  useEffect(() => {
    const fetchDesignRequestsList = async () => {
      setIsLoading(true)
      try {
        let data: DesignRequest[]
        if (isAdmin) {
          data = await getAdsPendingDesign(instance)
        } else if (session?.organizationId) {
          data = await fetchDesignRequests(instance, session.organizationId)
        } else {
          data = []
        }
        setDesignRequests(data)
      } catch (error) {
        notifications.show({
          title: 'Error loading design requests',
          message: 'Unable to load design requests. Please try again.',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchDesignRequestsList()
  }, [instance, session, isAdmin])

  const handleApproveDesign = async (requestId: number) => {
    try {
      setApprovingRequestId(requestId)
      await approveDesignRequest(instance, requestId)

      setDesignRequests((prev) =>
        prev.filter((request) => request.id !== requestId),
      )

      notifications.show({
        title: 'Success',
        message: 'Design has been approved',
        color: 'green',
      })
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to approve design',
        color: 'red',
      })
    } finally {
      setApprovingRequestId(null)
    }
  }

  const handleViewDetails = (requestId: number) => {
    navigate(
      isAdmin
        ? `/admin/designRequests/${requestId}`
        : `/dashboard/designRequests/${requestId}`,
    )
  }

  const handleRequestChanges = async (changes: string) => {
    if (!selectedRequestId) return

    try {
      setRequestingChanges(true)
      await requestDesignChanges(instance, selectedRequestId, changes)

      setDesignRequests((prev) =>
        prev.map((request) =>
          request.id === selectedRequestId
            ? { ...request, status: AdStatuses.ChangesRequested }
            : request,
        ),
      )

      notifications.show({
        title: 'Changes Requested',
        message: 'We will review your request and get back to you soon.',
        color: 'green',
      })
      setShowChangesModal(false)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to request changes',
        color: 'red',
      })
    } finally {
      setRequestingChanges(false)
      setSelectedRequestId(null)
    }
  }

  const pendingDesignRequests = designRequests.filter(
    (request) => request.status === AdStatuses.PendingDesign,
  )
  const pendingApprovalRequests = designRequests.filter(
    (request) => request.status === AdStatuses.PendingAdvertiserApproval,
  )
  const changesRequestedRequests = designRequests.filter(
    (request) => request.status === AdStatuses.ChangesRequested,
  )

  const renderRequestCard = (request: DesignRequest) => (
    <Paper
      key={request.id}
      withBorder
      p='md'
      radius='md'
      style={{ cursor: 'pointer' }}
      onClick={() => handleViewDetails(request.id)}
    >
      <Group align='flex-start' gap='xl'>
        {(request.status === AdStatuses.PendingAdvertiserApproval ||
          request.status === AdStatuses.ChangesRequested) && (
          <AspectRatio ratio={16 / 9} style={{ position: 'relative' }}>
            <Image
              src={request.advertisementUrl}
              alt={request.advertisementName}
              width={175}
              height={175}
              radius='md'
            />
            <ActionIcon
              variant='filled'
              radius='md'
              visibleFrom='sm'
              onClick={(e) => {
                e.stopPropagation()
                setSelectedAd(request.advertisementUrl)
              }}
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={faExpand}
                style={{ fontSize: '14px', color: 'white' }}
              />
            </ActionIcon>
          </AspectRatio>
        )}
        <Stack gap={4} style={{ flex: 1 }}>
          <Title order={3}>{request.advertisementName}</Title>
          <Text c='dimmed'>{request.locationName}</Text>
          <Text size='sm'>{request.deviceName}</Text>
          <Text size='sm' c='dimmed'>
            Submitted:{' '}
            {convertServerDateToLocal(
              request.createdDate,
            )?.toLocaleDateString()}
          </Text>
        </Stack>
        {request.status === AdStatuses.PendingAdvertiserApproval && !isAdmin ? (
          <Group gap='md'>
            <Text
              component='button'
              onClick={(e) => {
                e.stopPropagation()
                setSelectedRequestId(request.id)
                setShowChangesModal(true)
              }}
              c={COLORS.HiddenGemzRed}
              style={{
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              Request Changes
            </Text>
            <Button
              color={COLORS.LightGreen}
              onClick={(e) => {
                e.stopPropagation()
                handleApproveDesign(request.id)
              }}
              loading={approvingRequestId === request.id}
              loaderProps={{ type: 'dots' }}
            >
              Approve Design
            </Button>
          </Group>
        ) : (
          <FontAwesomeIcon icon={faChevronRight} color={COLORS.LightGreen} />
        )}
      </Group>
    </Paper>
  )

  if (isLoading) {
    return (
      <Stack gap='xl'>
        <DashboardHeader title='Design Requests' />

        <Stack gap='xl' px={24} pb={24}>
          <Stack gap='md'>
            <Group gap='xs'>
              <Skeleton height={32} width={300} />
            </Group>
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} height={120} radius='md' />
            ))}
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <Stack gap='xl'>
        {!isAdmin && (
          <Group
            justify='space-between'
            align='center'
            h={{ base: 'auto', sm: 70 }}
            px={24}
            py={{ base: 16, sm: 0 }}
            style={{
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
              Design Requests
            </Title>
          </Group>
        )}

        <Stack gap='xl' px={24} pb={24} pt={isAdmin ? 24 : 0}>
          {pendingApprovalRequests.length > 0 && (
            <Stack gap='md'>
              <Group gap='xs'>
                <Image
                  src={getStatusIcon(AdStatuses.PendingAdvertiserApproval)}
                  width={24}
                  height={24}
                  alt={AdStatuses.PendingAdvertiserApproval}
                />
                <Title order={4} c={COLORS.LightGreen}>
                  {getStatusTitle(
                    AdStatuses.PendingAdvertiserApproval,
                    isAdmin,
                  )}
                </Title>
              </Group>
              {pendingApprovalRequests.map(renderRequestCard)}
            </Stack>
          )}
          {changesRequestedRequests.length > 0 && (
            <Stack gap='md'>
              <Group gap='xs'>
                <FontAwesomeIcon
                  icon={faPencil}
                  style={{
                    color: COLORS.LightGreen,
                    width: '24px',
                    height: '24px',
                  }}
                />
                <Title order={4} c={COLORS.LightGreen}>
                  Changes Requested
                </Title>
              </Group>
              {changesRequestedRequests.map(renderRequestCard)}
            </Stack>
          )}
          {pendingDesignRequests.length > 0 && (
            <Stack gap='md'>
              <Group gap='xs'>
                <FontAwesomeIcon
                  icon={faPaintBrush}
                  style={{
                    color: COLORS.LightGreen,
                    width: '24px',
                    height: '24px',
                  }}
                />
                <Title order={4} c={COLORS.LightGreen}>
                  {getStatusTitle(AdStatuses.PendingDesign, isAdmin)}
                </Title>
              </Group>
              {pendingDesignRequests.map(renderRequestCard)}
            </Stack>
          )}

          {designRequests.length === 0 && (
            <Stack align='center' gap='md' py='xl'>
              <Text size='lg' c='dimmed' ta='center'>
                No design requests to show
              </Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Modal
        opened={!!selectedAd}
        onClose={() => setSelectedAd(null)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        >
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setSelectedAd(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
          {selectedAd && (
            <div
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'calc(90vh * 16/9)',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={selectedAd}
                alt='Advertisement'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      <RequestChangesModal
        isOpen={showChangesModal}
        onClose={() => {
          setShowChangesModal(false)
          setSelectedRequestId(null)
        }}
        onConfirm={handleRequestChanges}
        isSubmitting={requestingChanges}
      />
    </>
  )
}
